import { useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../config/firebase';
import type { Post } from '../types/calendar';

interface CreatePostInput {
  formData?: FormData;
  json?: PublishPostPayload;
}

// Type for standard JSON payload
interface PublishPostPayload {
  brandId: string;
  content: string;
  format: 'text' | 'video' | 'image';
  platforms: string[];
  media?: {
    url: string;
    type: 'video' | 'image';
    thumbnailUrl?: string;
  };
  platformSettings?: {
    tiktok?: {
      privacy_level: 'SELF_ONLY' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'PUBLIC_TO_EVERYONE';
      disable_duet: boolean;
      disable_comment: boolean;
      disable_stitch: boolean;
      video_cover_timestamp_ms: number;
    };
    youtube?: {
      title: string;
      tags: string[];
      privacyStatus: string;
      contentOwner: boolean;
    };
    linkedin?: {
      organizationId: string;
      visibility: string;
    };
  };
  publishNow?: boolean;
  scheduledFor?: string | null;
}

interface PublishResponse {
  success: boolean;
  post?: Post;
  results?: {
    [platform: string]: {
      success: boolean;
      data?: any;
      error?: any;
      message?: string;
      id?: string;
      status?: string;
      url?: string;
    }
  };
  publishResults?: {
    success: boolean;
    results: {
      [key: string]: {
        success: boolean;
        id?: string;
        status?: string;
        url?: string;
        message?: string;
        error?: string;
      };
    };
    message?: string;
  };
  error?: string;
  message?: string;
  response?: any;
}

// Create a post (draft or scheduled)
export const useCreatePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: CreatePostInput): Promise<PublishResponse> => {
      try {
        const token = await auth.currentUser?.getIdToken(true);
        
        if (!token) {
          throw new Error('No authentication token available');
        }

        const headers: Record<string, string> = {
          'Authorization': `Bearer ${token}`
        };

        let body: FormData | string;

        // If we have FormData, use multipart/form-data
        if (input.formData) {
          body = input.formData;
        } else if (input.json) {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify(input.json);
        } else {
          throw new Error('Invalid input: must provide either formData or json');
        }

        console.log('🚀 Creating post:', {
          url: 'https://api.socialness.ai/management/posts',
          hasMedia: !!input.formData,
          publishNow: false,
          scheduledFor: input.json?.scheduledFor
        });
        
        const response = await fetch('https://api.socialness.ai/management/posts', {
          method: 'POST',
          headers,
          body
        });
        
        console.log('📡 API Response status:', response.status);
        
        if (!response.ok) {
          let errorMessage = 'Failed to create post';
          let errorData;
          
          try {
            errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
            return { success: false, error: errorMessage, response: errorData };
          } catch (parseError) {
            errorMessage = `${response.status} ${response.statusText}`;
            return { success: false, error: errorMessage };
          }
        }
        
        const data = await response.json();
        console.log('✅ Post created successfully:', data);
        
        return {
          success: true,
          ...data
        };
      } catch (error) {
        console.error('💥 Error creating post:', error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to create post'
        };
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['posts'] });
      }
    }
  });
};

// Publish an existing post by ID
export const usePublishExistingPost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (postId: string): Promise<PublishResponse> => {
      try {
        const token = await auth.currentUser?.getIdToken(true);
        
        if (!token) {
          throw new Error('No authentication token available');
        }

        console.log(`🚀 Publishing existing post ID: ${postId}`);
        
        const apiUrl = `https://api.socialness.ai/posts/${postId}/publish`;
        console.log(`📡 Making API request to: ${apiUrl}`);
        
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        
        console.log('🔑 Request headers:', Object.keys(headers).join(', '));
        
        try {
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            // Adding a flag to help debugging cache issues
            cache: 'no-store'
          });
          
          console.log('📡 Publish API Response status:', response.status, response.statusText);
          console.log('📡 Response headers:', JSON.stringify([...response.headers.entries()]));
          
          // First, check content type to handle different response types properly
          const contentType = response.headers.get('content-type');
          console.log('📡 Response content type:', contentType);
          
          if (!response.ok) {
            // Handle CloudFlare errors (HTML responses)
            if (contentType && contentType.includes('text/html')) {
              console.error('❌ Received HTML error response (likely CloudFlare)');
              // For HTML responses, extract status code and provide a clearer error message
              return { 
                success: false, 
                error: `Server error (${response.status}). Please try again later.` 
              };
            }
            
            let errorMessage = 'Failed to publish post';
            let errorData;
            
            try {
              // Try to parse as JSON
              errorData = await response.json();
              console.error('❌ API Error Response:', errorData);
              errorMessage = errorData.error || errorData.message || errorMessage;
              return { success: false, error: errorMessage, response: errorData };
            } catch (parseError) {
              // If not JSON, try to get text content
              console.error('❌ API Error (not JSON):', response.statusText, parseError);
              
              try {
                const textResponse = await response.text();
                console.error('Response as text:', textResponse.substring(0, 500)); // Limit log size
                
                // If it looks like HTML, provide a clearer error
                if (textResponse.includes('<!DOCTYPE html>') || textResponse.includes('<html')) {
                  return { 
                    success: false, 
                    error: `Server error (${response.status}). Please try again later.`
                  };
                }
                
                return { success: false, error: textResponse.substring(0, 200) };
              } catch (textError) {
                console.error('Could not read response as text either:', textError);
                return { 
                  success: false, 
                  error: `${response.status} ${response.statusText}`
                };
              }
            }
          }
          
          // Successfully got response, now parse it
          // First, check if it's JSON
          if (contentType && contentType.includes('application/json')) {
            try {
              const data = await response.json();
              console.log('✅ Post published successfully. Response data:', JSON.stringify(data, null, 2));
              
              // Special handling for TikTok responses
              // If the response has results.tiktok.success = true, consider it a success
              // even if there's an error object
              if (data.results && data.results.tiktok) {
                const tiktokResult = data.results.tiktok;
                
                // Check if TikTok was successful
                if (tiktokResult.success === true) {
                  console.log('✅ TikTok publication initiated successfully:', tiktokResult);
                  
                  // If this is a TikTok-only publication or we want to prioritize TikTok success
                  const otherPlatformErrors = Object.entries(data.results)
                    .filter(([platform, result]: [string, any]) => 
                      platform !== 'tiktok' && !result.success
                    );
                    
                  if (otherPlatformErrors.length === 0) {
                    // If TikTok succeeded and there are no other platform errors,
                    // consider the entire operation successful
                    return {
                      success: true,
                      message: "Publication initiated on TikTok",
                      results: data.results
                    };
                  }
                }
              }

              // Check for platform-specific errors
              if (data.results) {
                const platformErrors = Object.entries(data.results)
                  .filter(([platform, result]: [string, any]) => {
                    // Skip TikTok if it has success: true, regardless of error object
                    if (platform === 'tiktok' && result.success === true) {
                      return false;
                    }
                    // For other platforms, only report as error if success is false
                    return !result.success;
                  })
                  .map(([platform, result]: [string, any]) => {
                    let errorMessage;
                    
                    if (typeof result.error === 'object' && result.error !== null) {
                      try {
                        errorMessage = JSON.stringify(result.error);
                      } catch (e) {
                        errorMessage = '[object Object]';
                      }
                    } else {
                      errorMessage = result.error || result.message || 'Unknown error';
                    }
                    
                    return `${platform}: ${errorMessage}`;
                  });

                if (platformErrors.length > 0) {
                  // If there are platform errors but TikTok was successful,
                  // we still want to return a partial success
                  const hasTikTokSuccess = data.results.tiktok && data.results.tiktok.success === true;
                  
                  if (hasTikTokSuccess) {
                    return {
                      // Mark as success if TikTok succeeded, even if other platforms failed
                      success: true,
                      message: "Publication initiated on TikTok, but failed on some other platforms",
                      error: `Failed to publish to some platforms:\n${platformErrors.join('\n')}`,
                      results: data.results
                    };
                  }
                  
                  return {
                    success: false,
                    error: `Failed to publish to some platforms:\n${platformErrors.join('\n')}`,
                    results: data.results
                  };
                }
              }
              
              // If we get here, everything was successful
              return {
                success: true,
                ...data
              };
            } catch (jsonError) {
              console.error('💥 Error parsing JSON response:', jsonError);
              return {
                success: false,
                error: 'Invalid response from server'
              };
            }
          } else {
            // Non-JSON response
            try {
              const textResponse = await response.text();
              console.log('📄 Non-JSON response:', textResponse.substring(0, 500));
              
              // If successful but not JSON, return simple success
              if (response.ok) {
                return {
                  success: true,
                  message: 'Post published successfully'
                };
              } else {
                return {
                  success: false,
                  error: 'Server returned invalid response format'
                };
              }
            } catch (textError) {
              console.error('💥 Error reading response:', textError);
              return {
                success: false,
                error: 'Failed to read server response'
              };
            }
          }
        } catch (fetchError) {
          console.error('💥 Network error:', fetchError);
          return {
            success: false,
            error: fetchError instanceof Error 
              ? `Network error: ${fetchError.message}`
              : 'Network error connecting to server'
          };
        }
      } catch (error) {
        console.error('💥 Error in publish operation:', error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to publish post'
        };
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['posts'] });
      }
    }
  });
};